import { DateTime } from 'luxon'

/**
 * @summary ISO Date 를 format 형식으로 변환
 */
export function formatISODate(date: string, format: string) {
  return DateTime.fromISO(date).toFormat(format)
}

/**
 * @summary UTC offset 이 반영된 시간을 Datetime 형식으로 변환
 */
export function fromISOOffsetDate(date: string): DateTime {
  return DateTime.fromISO(date, { setZone: true })
}

/**
 * @summary UTC offset 이 반영된 시간을 한국 시간으로 변환
 */
export function fromISOOffsetDateToKoreanTime(date: string): DateTime {
  return DateTime.fromISO(date, { zone: 'Asia/Seoul' })
}

/**
 * @summary UTC offset 이 반영된 시간을 format 형식으로 변환
 */
export function formatISOOffsetDate(date: string, format: string): string {
  return fromISOOffsetDate(date).toFormat(format)
}

/**
 * @summary yanolja time format validation
 * @description 00:00 ~ 02:00 까지 발생한 예약 건을 전일자 매출에 포함
 *              26시 까지 전일로 간주 ( -24H )
 *              HH:mm:ss or HH:mm 형식
 */
export const getIsYanoljaTimeFormat = (time: string): boolean => {
  const [hours, minutes, seconds = '00'] = time.split(':')
  const timeValue = [`${+hours >= 24 ? `0${+hours - 24}` : hours}`, minutes, seconds].join(':')
  const isAfterYanoljaTime = +hours >= 26 && (+minutes || +seconds)

  return DateTime.fromFormat(timeValue, 'HH:mm:ss').isValid && !isAfterYanoljaTime
}

/**
 * @description fromFormat 을 toFormat 으로 변환
 * */
export const formatFromTo = (date: string, fromFormat: string, toFormat: string): string => {
  return DateTime.fromFormat(date, fromFormat).toFormat(toFormat)
}

type TimeFormat = {
  date: string
  time: string
}
/**
 * ex)
 * 2021.06.07 (화) 23:10 ~ 26:00
 * 2021.06.07 (화) 11:10 ~ 12:00
 * @param from
 * @param to
 */
export const periodWithDayOrWeekFormat = ({ from, to }: { from: TimeFormat; to: string }) =>
  `${toFullDateWithDayOrWeek(from)} ~ ${toTimeWithAfterDay({ from, to })}`

/**
 * yyyy.MM.dd (ccc) HH:mm
 * @param date
 * @param time
 */
export const toFullDateWithDayOrWeek = ({ date, time }: TimeFormat): string => {
  return `${formatISODate(`${date}T00:00:00`, 'yyyy.MM.dd (ccc)')} ${afterDayTimeFormat(time)}`
}
export const toTimeWithAfterDay = ({ from, to }: { from: TimeFormat; to: string }) => {
  const dateTimeTo = DateTime.fromISO(to)
  const diff = dateTimeTo
    .endOf('day')
    .diff(DateTime.fromISO(`${from.date}T00:00:00`).endOf('day'), ['days'])
  return afterDayTimeFormat(dateTimeTo.toFormat('HH:mm'), diff.days >= 1)
}

/**
 * 시간 포맷
 * @param time
 * @param isAfterDay
 */
export const afterDayTimeFormat = (time: string, isAfterDay?: boolean) => {
  const [hour, minutes] = time.split(':')
  return `${isAfterDay ? +hour + 24 : hour}:${minutes}`
}

/**
 * @summary ISO Date 형식으로 변환
 */
export const toISOByDateFormat = (date: string, format = 'yyyy-MM-dd HH:mm:ss') => {
  return DateTime.fromFormat(date, format).setZone('Asia/Seoul').toISO()
}

/** 현재 시간이 시작시간과 종료시간 사이에 있는지 확인 */
export const isCurrentTimeBetween = (startTimeStr: string, endTimeStr: string): boolean => {
  const now = DateTime.now().setZone('Asia/Seoul') // 현재 시간
  const today = now.toISODate() // 오늘 날짜 (YYYY-MM-DD)

  let startDateTime = DateTime.fromISO(`${today}T${startTimeStr}`).setZone('Asia/Seoul')
  let endDateTime = DateTime.fromISO(`${today}T${endTimeStr}`).setZone('Asia/Seoul')

  // endTime이 startTime보다 작으면 다음 날로 간주 (예: 23:40 ~ 00:00)
  if (endDateTime < startDateTime) {
    endDateTime = endDateTime.plus({ days: 1 })
  }

  // 현재 시간이 시작 시간보다 이른 경우, 시간 범위를 하루 전으로 조정
  // 예: 현재 00:30, 범위 "오늘 23:40 ~ 내일 01:00" → 어제 23:40 ~ 오늘 01:00로 조정
  if (now < startDateTime) {
    startDateTime = startDateTime.minus({ days: 1 })
    endDateTime = endDateTime.minus({ days: 1 })
  }

  return now >= startDateTime && now <= endDateTime
}

/** 현재 시간이 날짜와 시간을 조합한 범위 사이에 있는지 확인 */
export const isCurrentTimeBetweenDateRange = ({
  startDate,
  endDate,
  startTime,
  endTime,
}: {
  startDate: string
  endDate: string
  startTime: string
  endTime: string
}): boolean => {
  const now = DateTime.now().setZone('Asia/Seoul')

  const startDateTime = DateTime.fromISO(`${startDate}T${startTime}`).setZone('Asia/Seoul')
  const endDateTime = DateTime.fromISO(`${endDate}T${endTime}`).setZone('Asia/Seoul')

  return now >= startDateTime && now <= endDateTime
}

/**
 * 현재 시간이 지정된 타임존의 시작과 끝 시간 사이에 있는지 확인합니다.
 * @param start - 시작 시간 문자열 (YYYY-MM-DD HH:mm:ss)
 * @param end - 종료 시간 문자열 (YYYY-MM-DD HH:mm:ss)
 * @param zone - IANA 타임존. 기본값은 'Asia/Seoul'입니다.
 * @returns {boolean} 범위 내에 있으면 true, 아니면 false
 */
export const isNowBetween = (start: string, end: string, zone: string = 'Asia/Seoul'): boolean => {
  const now = DateTime.now().setZone(zone)
  const startDt = DateTime.fromSQL(start, { zone })
  const endDt = DateTime.fromSQL(end, { zone })

  if (!startDt.isValid || !endDt.isValid) {
    console.error('날짜 형식이 올바르지 않습니다:', startDt.invalidReason || endDt.invalidReason)
    return false
  }

  return now >= startDt && now < endDt
}
